import { useCallback, useEffect, useRef } from 'react'

import { INITIAL_FIELD, INITIAL_FIELD_KEY } from '../constants'

export function useActions(
  value: Record<string, string>,
  apiChange: (value: Record<string, string>) => void,
) {
  const fieldValueRef = useRef(value)
  useEffect(() => {
    fieldValueRef.current = value
  }, [value])

  const onAddCustomField = useCallback(() => {
    const hasEmptyField = Object.keys(value).some(val => val === INITIAL_FIELD_KEY)
    if (!hasEmptyField) {
      // Merge the existing fields with a new initial field
      const newCustomField = { ...fieldValueRef.current, ...INITIAL_FIELD } as Record<
        string,
        string
      >

      apiChange(newCustomField)
    }
  }, [apiChange, value])

  const onUpdateSelect = useCallback(
    (newKey: string, index: number) => {
      // Update the key at the specified index
      const entries = Object.entries(value)
      entries[index] = [newKey, entries[index][1]]
      // Reconstruct the object from entries
      const updated = Object.fromEntries(entries)

      apiChange(updated)
    },
    [apiChange, value],
  )

  const onUpdateValue = useCallback(
    (newValue: string, index: number) => {
      // Update the value at the specified index
      const entries = Object.entries(value)
      entries[index] = [entries[index][0], newValue]
      // Reconstruct the object from entries
      const updated: Record<string, string> = {}
      entries.forEach(([k, v]) => {
        updated[k] = v
      })

      apiChange(updated)
    },
    [apiChange, value],
  )

  const onRemoveCustomField = useCallback(
    (index: number) => {
      // Remove the entry at the specified index
      const entries = Object.entries(value)
      entries.splice(index, 1)
      // Reconstruct the object from entries
      const updated: Record<string, string> = {}
      entries.forEach(([k, v]) => {
        updated[k] = v
      })

      apiChange(updated)
    },
    [apiChange, value],
  )

  return {
    onAddCustomField,
    onUpdateSelect,
    onUpdateValue,
    onRemoveCustomField,
  }
}
